"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, X, Images } from "lucide-react";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";
import { getMediumUrl, getLargeUrl, getThumbnailUrl } from "@/lib/utils/image-url";
import { useIsClient } from "@/hooks/use-is-client";

interface ListingGalleryProps {
  images: string[];
  title: string;
  isMobile?: boolean;
}

// Fullscreen Lightbox Component
function FullscreenLightbox({
  images,
  title,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  onIndexChange,
}: {
  images: string[];
  title: string;
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onIndexChange: (index: number) => void;
}) {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      onNext();
    }
    if (isRightSwipe && currentIndex > 0) {
      onPrev();
    }
  };

  // Klavye gezinimi + ODAK YÖNETİMİ
  //
  // Eskiden burada yalnızca ok/Escape dinleyicisi vardı: odak hiç içeri
  // taşınmıyordu, yani galeri açıkken Tab ARKA PLANDAKİ sayfada dolaşıyordu.
  // Kullanıcı görmediği bağlantılara odaklanıyor, kapatınca odak başa dönüyordu.
  // `role="dialog" aria-modal="true"` işaretlemesi bu davranışı DOĞRU sanmaya
  // yol açıyordu — işaretleme söz veriyor, kod tutmuyordu.
  //
  // Radix Dialog'a taşımak yerine tuzak elle kuruldu: mevcut z-[100] katmanı,
  // portal hedefi ve mobil bottom-sheet sıralaması AYNEN korunuyor.
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const oncekiOdak = document.activeElement as HTMLElement | null;
    const kap = dialogRef.current;

    /** Kaptaki odaklanabilir, görünür öğeler. */
    const odaklanabilirler = (): HTMLElement[] => {
      if (!kap) return [];
      return Array.from(
        kap.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") return onNext();
      if (e.key === "ArrowLeft") return onPrev();
      if (e.key === "Escape") return onClose();
      if (e.key !== "Tab") return;

      const liste = odaklanabilirler();
      if (liste.length === 0) return;
      const ilk = liste[0];
      const son = liste[liste.length - 1];
      const aktif = document.activeElement as HTMLElement | null;

      // Döngü: son öğeden Tab → ilke, ilkten Shift+Tab → sona.
      if (e.shiftKey && (aktif === ilk || !kap?.contains(aktif))) {
        e.preventDefault();
        son.focus();
      } else if (!e.shiftKey && (aktif === son || !kap?.contains(aktif))) {
        e.preventDefault();
        ilk.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    // Arka planı ekran okuyucudan ve sekme sırasından çıkar. Yalnızca
    // dokunduğumuz kardeşleri kaydedip temizlikte AYNEN geri alıyoruz.
    const inertYapilanlar: HTMLElement[] = [];
    const portalKok = kap?.parentElement ?? null;
    for (const kardes of Array.from(document.body.children)) {
      if (kardes === portalKok || !(kardes instanceof HTMLElement)) continue;
      if (kardes.hasAttribute("inert")) continue; // başkası koymuş, dokunma
      kardes.setAttribute("inert", "");
      inertYapilanlar.push(kardes);
    }

    // İlk odak kapatma butonuna: kullanıcı galeriye girdiğini duyar ve
    // Escape'i bilmese bile ilk Tab'da çıkış yolunu bulur.
    odaklanabilirler()[0]?.focus();

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      for (const el of inertYapilanlar) el.removeAttribute("inert");
      // Odağı galeriyi AÇAN öğeye geri ver.
      oncekiOdak?.focus?.();
    };
  }, [onNext, onPrev, onClose]);

  return createPortal(
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[100] bg-black"
      role="dialog"
      aria-modal="true"
      aria-label="Fotoğraf galerisi"
    >
      {/* Close Button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
        aria-label="Galeriyi kapat"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Main Image Area */}
      <div
        className="flex h-full w-full items-center justify-center"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Previous Button */}
        <button
          onClick={onPrev}
          disabled={currentIndex === 0}
          className={cn(
            "absolute left-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-all hover:bg-white/20",
            "md:left-6 md:p-4",
            currentIndex === 0 && "cursor-not-allowed opacity-30"
          )}
          aria-label="Önceki fotoğraf"
        >
          <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
        </button>

        {/* Image */}
        <div className="relative h-full w-full">
          <Image
            src={getLargeUrl(images[currentIndex])}
            alt={`${title} - ${currentIndex + 1}`}
            fill
            className="object-contain p-4 md:p-8"
            sizes="100vw"
            priority
          />
        </div>

        {/* Next Button */}
        <button
          onClick={onNext}
          disabled={currentIndex === images.length - 1}
          className={cn(
            "absolute right-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-all hover:bg-white/20",
            "md:right-6 md:p-4",
            currentIndex === images.length - 1 && "cursor-not-allowed opacity-30"
          )}
          aria-label="Sonraki fotoğraf"
        >
          <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
        </button>
      </div>

      {/* Counter */}
      <div className="absolute bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-sm font-medium text-white md:bottom-24 md:text-base">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Thumbnails - Desktop Only */}
      <div className="absolute bottom-4 left-1/2 z-50 hidden -translate-x-1/2 md:block">
        <div className="flex gap-2 rounded-xl bg-black/50 p-2 backdrop-blur-sm">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => onIndexChange(index)}
              className={cn(
                "relative h-14 w-16 shrink-0 overflow-hidden rounded-lg transition-all",
                index === currentIndex ? "ring-2 ring-white" : "opacity-50 hover:opacity-100"
              )}
              aria-label={`${index + 1}. fotoğrafa git`}
            >
              <Image
                src={getThumbnailUrl(image)}
                alt={`${title} - ${index + 1}`}
                fill
                sizes="64px"
                className="object-cover"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Dots */}
      <div className="absolute bottom-6 left-1/2 z-50 flex -translate-x-1/2 gap-1.5 md:hidden">
        {images.slice(0, Math.min(images.length, 10)).map((_, i) => (
          <button
            key={i}
            onClick={() => onIndexChange(i)}
            className={cn(
              "h-2 w-2 rounded-full transition-colors",
              i === currentIndex ? "bg-white" : "bg-white/40"
            )}
            aria-label={`${i + 1}. fotoğrafa git`}
          />
        ))}
        {images.length > 10 && <span className="text-xs text-white/60">+{images.length - 10}</span>}
      </div>
    </div>,
    document.body
  );
}

export function ListingGallery({ images, title, isMobile }: ListingGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const mounted = useIsClient();

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentImageIndex < images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
    if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => Math.min(prev + 1, images.length - 1));
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  // Mobile Gallery
  if (isMobile) {
    return (
      <>
        <div
          className="relative overflow-hidden rounded-2xl"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* KLAVYE ERİŞİMİ: eskiden `<div onClick>` idi — tabIndex/role yoktu,
              yani galeriyi klavyeyle açmanın tek yolu alttaki küçük butondu. */}
          <button
            type="button"
            className="relative block aspect-4/3 w-full cursor-pointer p-0"
            onClick={() => setLightboxOpen(true)}
            aria-label={`Fotoğrafı büyüt (${currentImageIndex + 1}/${images.length})`}
          >
            <Image
              src={getMediumUrl(images[currentImageIndex])}
              alt={title}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 70vw, 800px"
              className="object-cover"
              priority
            />
          </button>
          {/* Görsel sayacı */}
          <div className="text-foreground absolute right-3 bottom-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm">
            {currentImageIndex + 1}/{images.length}
          </div>
          <button
            onClick={() => setLightboxOpen(true)}
            className="text-foreground absolute bottom-3 left-3 flex items-center gap-1.5 rounded-lg bg-white/95 px-3 py-2 text-xs font-medium shadow-sm backdrop-blur-sm"
          >
            <Images className="h-3.5 w-3.5" />
            Tüm fotoğrafları göster
          </button>
        </div>

        {/* Mobile Fullscreen Lightbox */}
        {mounted && lightboxOpen && (
          <FullscreenLightbox
            images={images}
            title={title}
            currentIndex={currentImageIndex}
            onClose={() => setLightboxOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
            onIndexChange={setCurrentImageIndex}
          />
        )}
      </>
    );
  }

  // Desktop Gallery
  return (
    <>
      <div className="grid grid-cols-4 gap-2 overflow-hidden rounded-xl">
        {/* Eskiden `<div onClick>` İÇİNDE ayrı bir `<button>` vardı: hem klavyeyle
            erişilemeyen bir tıklama hedefi hem de iç içe interaktif yapı.
            Artık karonun kendisi tek bir <button>; "Tüm fotoğrafları göster"
            rozeti aynı eylemi yaptığı için <span>'e indirildi (görünüm aynı,
            iç içe buton yok). */}
        <button
          type="button"
          className="group relative col-span-2 row-span-2 block aspect-4/3 w-full cursor-pointer p-0"
          onClick={() => {
            setCurrentImageIndex(0);
            setLightboxOpen(true);
          }}
          aria-label={`Tüm fotoğrafları göster (${images.length} fotoğraf)`}
        >
          <Image
            src={getMediumUrl(images[0])}
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 60vw, 700px"
            className="object-cover transition-transform hover:scale-105"
            priority
          />
          <span className="text-foreground absolute bottom-4 left-4 flex items-center gap-2 rounded-lg bg-white/95 px-4 py-2.5 text-sm font-medium shadow-sm backdrop-blur-sm transition-colors group-hover:bg-white">
            <Images className="h-4 w-4" aria-hidden="true" />
            Tüm fotoğrafları göster
          </span>
        </button>
        {images.slice(1, 5).map((image, index) => (
          <button
            key={index}
            type="button"
            className="relative block aspect-4/3 w-full cursor-pointer overflow-hidden p-0"
            onClick={() => {
              setCurrentImageIndex(index + 1);
              setLightboxOpen(true);
            }}
            aria-label={`${index + 2}. fotoğrafı büyüt`}
          >
            <Image
              src={getMediumUrl(image)}
              alt={`${title} ${index + 2}`}
              fill
              sizes="(max-width: 768px) 50vw, 400px"
              className="object-cover transition-transform hover:scale-105"
            />
          </button>
        ))}
      </div>

      {/* Desktop Fullscreen Lightbox */}
      {mounted && lightboxOpen && (
        <FullscreenLightbox
          images={images}
          title={title}
          currentIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={nextImage}
          onPrev={prevImage}
          onIndexChange={setCurrentImageIndex}
        />
      )}
    </>
  );
}
